import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Fetch, API_BASE, authHeaders } from '../../utils/Utils.js';
import WeightLineChart from '../WeightLineChart.js';
import DateInput from '../../shared/inputs/DateInput.js';
import ErrorMessage from '../../shared/ErrorMessage.js';
import { compareWeighins, formatWeighDay } from '../weighins.js';
import './WeightLots.css';

const localDateStr = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
};

const StatCard = ({ label, value }) => (
  <div className="wl-stat-card">
    <div className="wl-stat-label">{label}</div>
    <div className="wl-stat-value">{value ?? '—'}</div>
  </div>
);

const WeightLotAnimalDetail = () => {
  const { lotId, tag } = useParams();
  const { t }          = useTranslation();
  const today          = localDateStr();

  const [weights,      setWeights]      = useState([]);
  const [newWeight,    setNewWeight]    = useState('');
  const [newDate,      setNewDate]      = useState(today);
  const [saving,       setSaving]       = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const load = useCallback(() => {
    Fetch(`api/data/weights?search_fields=tag&search_values=${tag}`, 'get', null,
      (data) => setWeights(
        (data || [])
          .filter(w => w.date && String(w.lot_id) === String(lotId))
          .sort(compareWeighins)
      )
    );
  }, [tag, lotId]);

  useEffect(() => { load(); }, [load]);

  const first  = weights[0]  ?? null;
  const latest = weights[weights.length - 1] ?? null;

  const adg = (() => {
    if (!first || !latest || first.date === latest.date) return null;
    const days = (new Date(latest.date) - new Date(first.date)) / 86400000;
    return days > 0 ? (latest.weight - first.weight) / days : null;
  })();

  const handleSubmit = async (e) => {
    e.preventDefault();
    const weightNum = parseFloat(newWeight);
    if (!weightNum || weightNum <= 0) return;
    setSaving(true);
    setErrorMessage('');
    try {
      const res = await fetch(`${API_BASE}/api/weight/entry`, {
        method: 'POST', headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ tag: tag, weight: weightNum, date: newDate, lot_id: Number(lotId) }),
      });
      if (!res.ok) { setErrorMessage(t('error_saving')); return; }
      setNewWeight(''); setNewDate(today); load();
    } catch {
      setErrorMessage(t('error_saving'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="wld-page">

      <div className="wld-header">
        <div className="wld-header-top">
          <div className="wl-detail-code">{t('tag')} #{tag}</div>
        </div>
      </div>

      <div className="wl-stats-grid">
        <StatCard label={t('first_weigh_date')}  value={first  ? formatWeighDay(first)  : null} />
        <StatCard label={t('first_weigh_value')} value={first  ? `${first.weight} kg`      : null} />
        <StatCard label={t('last_weigh_date')}   value={latest ? formatWeighDay(latest) : null} />
        <StatCard label={t('last_weigh_value')}  value={latest ? `${latest.weight} kg`     : null} />
        <StatCard label={t('adg_column')}        value={adg != null ? `${adg.toFixed(2)} kg/d` : null} />
      </div>

      <WeightLineChart
        points={weights.map(w => ({ date: w.date, weight: w.weight }))}
        label={`${t('tag')} #${tag}`}
      />

      <div style={{ marginTop: 24 }}>
        {weights.length === 0 ? (
          <div className="wl-empty-note">{t('no_weigh_ins')}</div>
        ) : (
          <div className="wl-sessions-list">
            {[...weights].reverse().map((w, i) => (
              <div key={i} className="wl-session-row">
                <span className="wl-session-date">{formatWeighDay(w)}</span>
                <span className="wl-session-weight">{w.weight} kg</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <form className="wl-inline-form" onSubmit={handleSubmit}>
        <input type="number" value={newWeight} onChange={e => setNewWeight(e.target.value)}
          placeholder={t('unit_kg')} min="1" max="9999" step="0.1" required />
        <DateInput value={newDate} onChange={(v) => setNewDate(v)} required />
        <button type="submit" className="wl-inline-btn" disabled={saving}>
          {saving ? '…' : '+'}
        </button>
      </form>
      <ErrorMessage message={errorMessage} />

    </div>
  );
};

export default WeightLotAnimalDetail;
